/**
 * Esquema del formulario de configuración.
 *
 * ! Es ESTÁTICO a propósito, y esa es la corrección de un defecto real. La primera versión derivaba
 * ! los campos de las llaves del objeto en tiempo de ejecución (`Object.keys(...).filter(k => typeof
 * ! obj[k] === 'number')`). Al vaciar un input, `p-inputNumber` escribe `null`, `typeof null` es
 * ! `'object'`, el campo se filtraba fuera del formulario **y ya no había forma de recuperarlo**.
 * ! Un formulario nunca debe derivar su estructura de sus valores.
 *
 * De aquí salen además el orden de los campos, sus etiquetas, sus unidades y toda la ayuda. Una sola
 * fuente: si se agrega un parámetro, se agrega aquí y aparece completo, con su explicación.
 */

/**
 * Ancla hacia el manual de negocio (Documentación → Análisis fiscal). Es solo el `id` de la sección:
 * el slug del documento lo pone el componente, así que renombrar el manual no obliga a tocar aquí.
 *
 * Existe porque la ayuda de esta pantalla era un callejón sin salida: explicaba el campo que tenías
 * delante y no había a dónde ir a entender el resto del cálculo.
 */
export type DocAnchor = 'resumen' | 'que-hace' | 'camino' | 'proveedores' | 'oferta' | 'no-alcanza' | 'fallas' | 'limites' | 'algoritmo'

export interface FieldHelp {
  /** Qué es, en una línea. */
  what: string
  /** Dónde entra en el cálculo, en términos de la fórmula. */
  where: string
  /** Un caso con números concretos. */
  example: string
  /** El efecto direccional. Es lo más accionable para quien no lee el código. */
  effect: string
}

export interface FieldDef {
  path: string
  label: string
  /** `switch` para las decisiones de sí/no. Por omisión, número. */
  kind?: 'number' | 'switch'
  /** Solo para `switch`: qué cambia al encenderlo y qué al apagarlo. Se muestra siempre, no en un tooltip. */
  onWarning?: string
  offWarning?: string
  unit?: string
  decimals: number
  min?: number
  max?: number
  help: FieldHelp
  /** Advertencia visible permanente. Reservado para los que pueden hacer daño callado. */
  danger?: string
  /** Sección del manual que explica este campo en términos de negocio. */
  docAnchor?: DocAnchor
}

/**
 * Sub-sección dentro de un bloque.
 *
 * Existe porque «Multiplicadores de riesgo» mezclaba dos cosas distintas en una sola parrilla: los
 * siete campos que arman la fórmula del score y los dos de listas negras. Nueve inputs seguidos sin
 * jerarquía se leen como una lista de números sueltos, no como dos ideas.
 */
export interface FieldGroup {
  label?: string
  hint?: string
  /**
   * Fórmula del grupo, con `{ruta.del.campo}` que se sustituye por el valor actual al renderizar.
   * Para un grupo cuyos campos solo tienen sentido juntos, ver la fórmula armada explica más que
   * los siete campos por separado.
   */
  formula?: string
  fields: FieldDef[]
}

export interface BlockDef {
  key: string
  label: string
  icon: string
  hint: string
  /** Sección del manual que explica este paso del cálculo. */
  docAnchor?: DocAnchor
  /** Explicación larga del bloque, con el paso del cálculo que representa. */
  help: string
  groups: FieldGroup[]
  /** Grupos de tramos que se editan con `config-tier-editor`. */
  tierGroups?: Array<{
    path: string
    label: string
    hint: string
    thresholdLabel: string
    thresholdUnit?: string
    thresholdStep?: number
    valueLabel: string
    valueUnit?: string
    valueStep?: number
    mode?: 'numeric' | 'grade'
    help: FieldHelp
  }>
}

export const FIELD_SCHEMA: BlockDef[] = [
  {
    key: 'providers',
    docAnchor: 'proveedores',
    label: 'Proveedores de datos',
    icon: 'fa-solid fa-plug',
    hint: 'De dónde salen los datos, y qué hacer cuando el principal no puede.',
    help:
      'Va **primero** porque ocurre antes que toda la aritmética: sin reporte no hay nada que calcular. Syntage es el principal y Ekatena el respaldo. ' +
      '**El respaldo todavía no está conectado**: lo que se ajuste aquí queda guardado y versionado, pero no cambia ninguna oferta hasta que el proveedor entre en línea. Lo que sí aplica hoy es la escala del score. ' +
      'Ekatena no cobra por suscripción sino **por consulta**: cada una descuenta créditos de un saldo finito. Es la primera vez que un proveedor puede *quedarse sin* antes que *caerse*, y por eso hay un freno de gasto que Syntage nunca necesitó. ' +
      'Un RFC mal escrito **no** cae al respaldo, y tampoco un cliente sin CIEC guardada: los dos proveedores necesitan lo mismo para poder consultar, así que preguntarle al segundo solo gastaría una llamada para obtener el mismo fallo con otro membrete. ' +
      'La escala del score no es un detalle cosmético: los tramos de tasa y los grades están calibrados sobre 850, así que un proveedor que puntúe sobre otro rango necesita su escala declarada aquí o su score caerá en tramos que no le tocan.',
    groups: [
      {
        fields: [
          {
            path: 'providers.fallbackEnabled',
            docAnchor: 'proveedores',
            label: 'Usar proveedor de respaldo',
            kind: 'switch',
            decimals: 0,
            onWarning:
              'Al ENCENDER: cuando Syntage no pueda entregar un reporte utilizable, se consulta a Ekatena antes de dar la verificación por fallida, y solo ante los fallos marcados abajo. Clientes que hoy se quedan sin oferta empezarán a recibir una, y esas ofertas quedan marcadas con su procedencia en la ficha. Cada consulta descuenta créditos de Ekatena.',
            offWarning: 'Al APAGAR: se vuelve al comportamiento actual, donde un fallo de Syntage deja al cliente sin oferta y sin más intentos. Es como la plataforma ha venido operando.',
            help: {
              what: 'Si se consulta a un segundo proveedor cuando el principal falla.',
              where: 'Actúa antes del cálculo: decide de qué reporte salen las señales.',
              example: 'Syntage no responde. Apagado, el cliente se queda sin oferta; encendido, se le pregunta a Ekatena con el mismo RFC y la misma CIEC.',
              effect: 'Encenderlo aumenta la cobertura y empieza a consumir créditos de Ekatena. No cambia ninguna oferta ya emitida.',
            },
          },
        ],
      },
      {
        label: 'Cuándo entra el respaldo',
        hint: 'Consultar al respaldo no cuesta lo mismo en todos los fallos. Estos interruptores solo tienen efecto con el respaldo encendido.',
        fields: [
          {
            path: 'providers.fallbackTriggers.unavailable',
            docAnchor: 'proveedores',
            label: 'Cuando el principal no responde',
            kind: 'switch',
            decimals: 0,
            onWarning:
              'Al ENCENDER: si Syntage se cae, la verificación continúa con Ekatena y el cliente recibe su oferta igual. Es el caso donde el respaldo más rinde: el problema es del proveedor, no del contribuyente.',
            offWarning: 'Al APAGAR: una caída de Syntage vuelve a ser el final del camino, aunque el respaldo esté encendido.',
            help: {
              what: 'Si una caída del proveedor principal justifica preguntarle al respaldo.',
              where: 'Se evalúa cuando el intento con Syntage termina en «no disponible».',
              example: 'Syntage devuelve un error de red a media mañana. Encendido, la verificación continúa con Ekatena y el cliente ni se entera.',
              effect: 'Es el disparador con mejor relación entre lo que cubre y lo que cuesta.',
            },
          },
          {
            path: 'providers.fallbackTriggers.noData',
            docAnchor: 'proveedores',
            label: 'Cuando el principal no trae datos suficientes',
            kind: 'switch',
            decimals: 0,
            onWarning:
              'Al ENCENDER: cuando Syntage responde pero sin datos con qué estimar la capacidad, se consulta a Ekatena. Leen fuentes distintas, así que uno puede tener lo que al otro le faltó. OJO: este interruptor no basta por sí solo — el fallo que lo dispara solo se produce si «Rechazar cuando no hay capacidad» está encendido, en la sección Interpretación de las señales. Con aquel apagado, este no llega a evaluarse nunca.',
            offWarning:
              'Al APAGAR: un reporte vacío de Syntage se acepta como respuesta final. Lo que pase después lo decide «Rechazar cuando no hay capacidad»: encendido, la verificación queda marcada sin oferta y con su motivo; apagado —como está hoy— se emite una oferta de $0 marcada como exitosa.',
            help: {
              what: 'Si un reporte sin señales de capacidad justifica pedir una segunda opinión.',
              where: 'Se evalúa cuando el intento con Syntage termina en «sin datos suficientes» — un desenlace que **solo existe si «Rechazar cuando no hay capacidad» está encendido**.',
              example: 'Un contribuyente recién dado de alta: Syntage no tiene historia suya todavía. Ekatena puede tener otra parte.',
              effect: 'Sube la cobertura en clientes nuevos o de poca facturación. Descuenta créditos sin garantía de encontrar nada.',
            },
          },
          {
            path: 'providers.fallbackTriggers.ciecRejected',
            docAnchor: 'proveedores',
            label: 'Cuando el SAT rechaza la CIEC',
            kind: 'switch',
            decimals: 0,
            onWarning:
              'Al ENCENDER: una CIEC rechazada se vuelve a intentar con Ekatena, como segunda opinión. Son dos lectores distintos del SAT y no siempre coinciden, pero Ekatena usa LA MISMA contraseña: si de verdad está mal, se gastan créditos para obtener el mismo rechazo.',
            offWarning: 'Al APAGAR (lo recomendado): una CIEC rechazada se le devuelve al cliente para que la capture de nuevo, que es lo que de verdad la corrige. No descuenta créditos.',
            help: {
              what: 'Si un rechazo de contraseña justifica gastar una consulta del respaldo.',
              where: 'Se evalúa cuando el intento con Syntage termina en «el SAT no aceptó esta CIEC».',
              example: 'El cliente cambió su contraseña del SAT la semana pasada y capturó la vieja. Ninguno de los dos proveedores va a poder entrar.',
              effect: 'Es el disparador con peor relación entre lo que cubre y lo que cuesta. Llega apagado a propósito.',
            },
            danger:
              'Se creyó que el respaldo no necesitaba la CIEC y por eso este era el caso más valioso. La documentación de Ekatena lo desmiente: su reporte exige RFC y CIEC, igual que Syntage. Encenderlo es apostar a que un proveedor acepte lo que el otro rechazó.',
          },
        ],
      },
      {
        label: 'Freno de gasto',
        hint: 'Cada consulta a Ekatena descuenta créditos de un saldo finito. Sin freno, una caída larga del principal lo agota.',
        formula: 'Se consulta al respaldo solo si  créditos − {providers.wallet.reportCost}  ≥  {providers.wallet.minBalance}',
        fields: [
          {
            path: 'providers.wallet.minBalance',
            docAnchor: 'proveedores',
            label: 'Reserva mínima de créditos',
            unit: 'créditos',
            decimals: 0,
            min: 0,
            max: 5000,
            help: {
              what: 'Los créditos de Ekatena que el respaldo automático nunca toca.',
              where: 'Antes de cada consulta al respaldo se compara el saldo real de Ekatena contra esta reserva.',
              example: 'Con reserva de 300 y reportes de 100: el respaldo deja de dispararse al llegar a 400, y quedan tres consultas para que Operaciones las use a mano.',
              effect: 'Subirla protege más el saldo y corta antes la cobertura automática. En 0 no hay freno.',
            },
            danger: 'En 0 una caída prolongada del principal puede consumir todos los créditos sin que nadie lo note hasta que el respaldo deje de servir.',
          },
          {
            path: 'providers.wallet.reportCost',
            docAnchor: 'proveedores',
            label: 'Costo de un reporte',
            unit: 'créditos',
            decimals: 0,
            min: 1,
            max: 1000,
            help: {
              what: 'Los créditos que descuenta cada consulta al respaldo.',
              where: 'Se resta del saldo para saber si la consulta lo dejaría por debajo de la reserva.',
              example: 'El plan contratado cobra 100 créditos por reporte, así que un saldo de 2,000 son veinte consultas.',
              effect: 'No cambia lo que cobra el proveedor: cambia lo que la plataforma cree que cobra.',
            },
            danger: 'Es un hecho del plan contratado, no una preferencia. Ponerlo por debajo de lo real hace que el freno deje pasar consultas que vacían la reserva.',
          },
        ],
      },
      {
        label: 'Escala del score de cada proveedor',
        hint: 'Sobre cuántos puntos califica cada uno. Los tramos de tasa y los grados están hechos sobre 850, así que cada score se convierte a esa base antes de usarse.',
        fields: [
          {
            path: 'providers.scoreScales.syntage',
            docAnchor: 'proveedores',
            label: 'Escala de Syntage',
            unit: 'pts',
            decimals: 0,
            min: 100,
            max: 2000,
            help: {
              what: 'El máximo de la escala con la que Syntage califica: 1,000 puntos.',
              where: 'Convierte su score a la base común de 850 antes de buscar su tramo de tasa y su grado.',
              example: 'Con 1,000, un score de 760 se lee como 646 sobre 850. Con 850 se leería como 760 y el cliente subiría de tramo sin merecerlo.',
              effect:
                'Cambiarlo mueve las ofertas: con el mismo score, el cliente cae en otro tramo de tasa y otro grado, y le cambian el monto y la tasa. Las ofertas ya emitidas no se recalculan; solo cambian los análisis nuevos.',
            },
            danger: 'Debe coincidir con la escala real de Syntage, que es de 1,000. Antes de activar una versión con otra escala, revisa cuánto mueve las ofertas de los clientes.',
          },
          {
            path: 'providers.scoreScales.ekatena',
            docAnchor: 'proveedores',
            label: 'Escala de Ekatena',
            unit: 'pts',
            decimals: 0,
            min: 100,
            max: 2000,
            help: {
              what: 'El máximo de la escala con la que Ekatena califica.',
              where: 'Convierte su score a la base de 850 antes de aplicar los tramos.',
              example: 'Un 963 sobre 1000 equivale a 818 sobre 850. Sin convertir caería en el tramo más alto, que no le toca.',
              effect: 'Es lo que impide que un score de otro proveedor se lea en la escala equivocada.',
            },
            danger:
              'La conversión entre escalas es LINEAL POR SUPOSICIÓN: que dos modelos compartan rango no significa que midan lo mismo. Se hace porque la alternativa —mezclarlas sin convertir— es peor y además muda.',
          },
        ],
      },
    ],
  },
  {
    key: 'baseCapacity',
    docAnchor: 'oferta',
    label: 'Capacidad base',
    icon: 'fa-solid fa-coins',
    hint: 'Cuatro lecturas independientes de cuánto puede pagar el cliente. Se toma la más favorable.',
    help:
      'Es el primer paso y el que fija el techo de todo lo demás. Se calculan cuatro estimaciones a partir de fuentes distintas del reporte del SAT —utilidad neta, ventas anualizadas, facturación promedio y flujo de efectivo— y se toma **la más alta**. ' +
      'Se usa la más alta y no el promedio porque cada fuente falla de una manera: un cliente puede no tener contabilidad electrónica pero sí facturación, o al revés. Quedarse con la mejor evita castigar a alguien por un hueco en los datos del proveedor, no en su negocio.',
    groups: [
      {
        label: 'Las cuatro estimaciones',
        hint: 'Cada una lee una fuente distinta del reporte. Se toma la más alta.',
        fields: [
          {
            path: 'baseCapacity.netIncomeMonths',
            label: 'Meses de utilidad',
            unit: 'meses',
            decimals: 0,
            help: {
              what: 'Cuántos meses de utilidad neta se consideran financiables. La utilidad es mensual: el promedio de los últimos 12 meses completos de flujo por CFDI (cobrado − pagado).',
              where: 'Estimación 1 = utilidad neta mensual × este número.',
              example: 'Con utilidad mensual de $220 k y 6 meses: 220 k × 6 = $1.3 M de capacidad por esta vía.',
              effect: 'Subirlo aumenta la capacidad en proporción directa. Es el parámetro que más rápido mueve el monto de las ofertas.',
            },
          },
          {
            path: 'baseCapacity.annualSalesFactor',
            label: 'Factor de ventas',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Qué proporción de las ventas de los últimos doce meses se considera apalancable.',
              where: 'Estimación 2 = ventas anualizadas × este factor.',
              example: 'Con ventas de $12 M y 0.25: 12 M × 0.25 = $3 M.',
              effect: 'Subirlo favorece a los clientes con mucha venta y poco margen. Bajarlo los castiga.',
            },
          },
          {
            path: 'baseCapacity.avgBillingFactor',
            label: 'Factor de facturación',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Qué proporción de la facturación promedio de los últimos tres años se considera apalancable.',
              where: 'Estimación 3 = facturación promedio × este factor.',
              example: 'Con promedio de $12 M y 0.35: 12 M × 0.35 = $4.2 M.',
              effect: 'Es la fuente más estable porque promedia tres años. Subirlo suaviza el efecto de un año malo.',
            },
          },
          {
            path: 'baseCapacity.cashFlowFactor',
            label: 'Factor de flujo',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Qué proporción del flujo de efectivo neto del periodo se considera financiable.',
              where: 'Estimación 4 = (ingresos − egresos de la ventana) × este factor.',
              example: 'Con flujo neto de $6 M y 0.30: 6 M × 0.30 = $1.8 M.',
              effect: 'Es la lectura más cercana a la capacidad real de pago mes a mes. Subirlo premia a quien de verdad genera caja, no solo factura.',
            },
          },
          {
            path: 'baseCapacity.cashFlowWindowMonths',
            label: 'Ventana del flujo',
            unit: 'meses',
            decimals: 0,
            help: {
              what: 'Cuántos meses hacia atrás se suman para calcular el flujo neto.',
              where: 'Acota qué movimientos entran a la estimación 4.',
              example: 'Con 12, se toma desde el mismo mes del año pasado.',
              effect: 'Una ventana corta reacciona rápido a un mal trimestre; una larga suaviza la estacionalidad.',
            },
          },
          {
            path: 'baseCapacity.salesSeriesWindowMonths',
            label: 'Ventana de ventas',
            unit: 'meses',
            decimals: 0,
            help: {
              what: 'Cuántos meses de la serie mensual de ventas se suman para anualizar.',
              where: 'Acota la estimación 2.',
              example: 'Con 12 se suman los últimos doce meses de venta.',
              effect: 'Igual que la anterior: corta reacciona, larga suaviza.',
            },
          },
        ],
      },
      {
        label: 'Suposiciones cuando faltan datos',
        hint: 'Solo entran en juego si el reporte del SAT no trae el dato real. Son supuestos, no mediciones.',
        fields: [
          {
            path: 'baseCapacity.fallbackNetIncomeRatio',
            label: 'Utilidad supuesta',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Qué utilidad se le supone a un cliente cuyo reporte no trae el desglose de ingresos contra gastos.',
              where: 'Utilidad supuesta = facturación promedio × este factor. Solo aplica si falta el dato real.',
              example: 'Con facturación de $12 M y 0.25, se le supone una utilidad de $3 M.',
              effect: 'Es una suposición sobre un cliente del que no tenemos el dato. Subirlo es asumir que a quien no le vemos las cuentas le va bien.',
            },
            danger: 'Aplica solo a clientes de los que NO tenemos el desglose real. Es una suposición, no una medición.',
          },
          {
            path: 'baseCapacity.fallbackMarginNoRevenue',
            label: 'Margen si ingresos = 0',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Margen que se asume cuando hay desglose pero los ingresos reportados son cero.',
              where: 'Evita una división entre cero al calcular el margen.',
              example: 'Con 0.20 se le asume un margen del 20%.',
              effect: 'Determina en qué tramo de plazo y de multiplicador por margen cae un caso sin ingresos reportados.',
            },
          },
          {
            path: 'baseCapacity.fallbackMarginNoIvG',
            label: 'Margen sin desglose',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Margen que se asume cuando el reporte no trae ingresos contra gastos en absoluto.',
              where: 'Alimenta el multiplicador por margen y el tramo de plazo.',
              example: 'Con 0.25 se le asume 25%, que en la configuración por defecto ya alcanza el plazo de 60 meses.',
              effect: 'Cuidado: un valor generoso aquí le regala el mejor plazo a quien no sabemos cómo está.',
            },
            danger: 'Con 0.25 o más, un cliente sin datos de rentabilidad alcanza el plazo máximo por suposición.',
          },
        ],
      },
    ],
  },
  {
    key: 'riskMultipliers',
    docAnchor: 'oferta',
    label: 'Multiplicadores de riesgo',
    icon: 'fa-solid fa-shield-halved',
    hint: 'Seis factores que se multiplican entre sí sobre la capacidad base.',
    help:
      'La capacidad base dice cuánto podría pagar el cliente si todo estuviera bien. Estos seis factores ajustan por lo que no está bien: score, tamaño, listas negras, concentración de clientes, concentración de proveedores y margen. ' +
      '**Se multiplican entre sí, no se promedian**, y eso castiga rápido: seis factores de 0.85 —cada uno modesto por separado— dejan la capacidad en 38% de la base. Al mover cualquiera de ellos, conviene mirar el efecto compuesto en la vista previa, no el factor aislado.',
    groups: [
      {
        label: 'Normalización del score',
        hint: 'Estos siete campos arman una sola fórmula. Por separado no dicen mucho; junta es esta:',
        formula:
          'factor = mín({riskMultipliers.score.cap}, máx({riskMultipliers.score.floor}, ((score − {riskMultipliers.score.minScore}) ÷ {riskMultipliers.score.span}) × {riskMultipliers.score.slope} + {riskMultipliers.score.intercept}))',
        fields: [
          {
            path: 'riskMultipliers.score.defaultScore',
            docAnchor: 'no-alcanza',
            label: 'Score supuesto',
            unit: 'pts',
            decimals: 0,
            danger: 'Aplica justo a los clientes de los que menos sabemos. Con 650 caen en el tramo del 29% como si los hubiéramos evaluado.',
            help: {
              what: 'El score que se asume cuando el proveedor no devuelve ninguno.',
              where: 'Sustituye al score real en TODO el cálculo: el factor, el tramo de tasa y el grade.',
              example: 'Con 650, un cliente sin score recibe factor 0.78 y tasa del 29%, idéntico a uno evaluado con 650 real.',
              effect: 'Es el parámetro más delicado de la configuración. Bajarlo es ser prudente con los desconocidos; subirlo es tratarlos como si los conociéramos.',
            },
          },
          {
            path: 'riskMultipliers.score.minScore',
            label: 'Score inicial',
            unit: 'pts',
            decimals: 0,
            help: {
              what: 'El score a partir del cual el factor empieza a crecer.',
              where: 'factor = ((score − este valor) ÷ rango) × pendiente + base.',
              example: 'Con 550, un score de 550 da el factor base; por debajo se queda en el piso.',
              effect: 'Subirlo hace que se necesite mejor score para salir del piso.',
            },
          },
          {
            path: 'riskMultipliers.score.span',
            label: 'Rango',
            unit: 'pts',
            decimals: 0,
            help: {
              what: 'Cuántos puntos de score cubre la rampa completa.',
              where: 'Es el divisor de la fórmula.',
              example: 'Con 300, la rampa va de 550 a 850.',
              effect: 'Un rango chico hace la curva más agresiva: pocos puntos de score cambian mucho el factor.',
            },
          },
          {
            path: 'riskMultipliers.score.slope',
            label: 'Pendiente',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Cuánto crece el factor a lo largo de toda la rampa.',
              where: 'Multiplica la proporción normalizada.',
              example: 'Con 0.55, ir de 550 a 850 suma 0.55 al factor.',
              effect: 'Subirlo premia más el buen score.',
            },
          },
          {
            path: 'riskMultipliers.score.intercept',
            label: 'Factor inicial',
            unit: '×',
            decimals: 2,
            help: {
              what: 'El factor que corresponde al score inicial de la rampa.',
              where: 'Es el término independiente de la fórmula.',
              example: 'Con 0.6, un score de 550 da factor 0.6.',
              effect: 'Subirlo levanta a todos por parejo.',
            },
          },
          {
            path: 'riskMultipliers.score.floor',
            label: 'Piso',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Lo más bajo que puede llegar el factor por score.',
              where: 'Se aplica después de la fórmula.',
              example: 'Con 0.6, ni un score de 0 baja del 0.6.',
              effect: 'Bajarlo permite castigar más a los peores scores.',
            },
          },
          {
            path: 'riskMultipliers.score.cap',
            label: 'Techo',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Lo más alto que puede llegar el factor por score.',
              where: 'Se aplica después de la fórmula.',
              example: 'Con 1.15, un score perfecto suma como máximo 15%.',
              effect: 'Sin techo, un score alto por sí solo podría inflar la capacidad más de lo que el resto de las señales sostiene.',
            },
          },
        ],
      },
      {
        label: 'Listas negras',
        hint: 'Uno de los seis multiplicadores. Se aplica entero, sin tramos.',
        fields: [
          {
            path: 'riskMultipliers.blacklist.penaltyValue',
            label: 'Con listas negras',
            unit: '×',
            decimals: 2,
            help: {
              what: 'El multiplicador que se aplica si el cliente aparece en listas negras.',
              where: 'Entra al producto de los seis factores.',
              example: 'Con 0.6, la capacidad se reduce al 60%.',
              effect: 'Es el castigo más fuerte del bloque. Bajarlo lo endurece.',
            },
          },
          {
            path: 'riskMultipliers.blacklist.cleanValue',
            label: 'Sin listas negras',
            unit: '×',
            decimals: 2,
            help: {
              what: 'El multiplicador cuando el cliente está limpio.',
              where: 'Entra al producto de los seis factores.',
              example: 'Con 1.0 no modifica nada, que es lo normal.',
              effect: 'Ponerlo arriba de 1 sería premiar por no estar señalado, no solo no castigar.',
            },
          },
        ],
      },
      {
        label: 'Cuando ningún tramo aplica',
        hint: 'El valor que recibe quien no alcanza ni el tramo más bajo de las tablas de abajo. No es 1: hoy los dos castigan, y el de Tamaño alcanza al 88% de los análisis.',
        fields: [
          {
            path: 'riskMultipliers.size.defaultValue',
            label: 'Tamaño · sin tramo',
            unit: '×',
            decimals: 2,
            danger: 'Es el factor que hoy recibe el 88% de los análisis. Moverlo mueve casi toda la cartera a la vez, no un caso de borde.',
            help: {
              what: 'El multiplicador por tamaño cuando el cliente no alcanza el tramo más bajo de empleados.',
              where: 'Lo devuelve `pickTier` al no encontrar tramo. Entra al producto de los seis factores igual que cualquier otro.',
              example: 'Con el tramo más bajo en 10 empleados y este valor en 0.90, una empresa de 4 —o una de la que no se pudo saber el dato— pierde 10% de capacidad.',
              effect: 'Es el que más cartera mueve de toda la pantalla. En 1.00 dejas de castigar el desconocimiento; por debajo de 0.90 lo endureces. Simula antes.',
            },
          },
          {
            path: 'riskMultipliers.margin.defaultValue',
            label: 'Margen · sin tramo',
            unit: '×',
            decimals: 2,
            help: {
              what: 'El multiplicador por margen cuando el cliente no alcanza el tramo más bajo.',
              where: 'Lo devuelve `pickTier` al no encontrar tramo. Entra al producto de los seis factores.',
              example: 'Con el tramo más bajo en 20% y este valor en 0.85, un margen del 12% pierde 15% de capacidad.',
              effect: 'Alcanza al 16.5% de los análisis. Un margen bajo sí es una señal real, así que aquí el castigo se defiende mejor que en Tamaño.',
            },
          },
        ],
      },
    ],
    tierGroups: [
      {
        path: 'riskMultipliers.size.tiers',
        label: 'Por número de empleados',
        hint: 'Proxy de tamaño y formalidad.',
        thresholdLabel: 'empleados',
        valueLabel: 'Factor',
        valueStep: 0.01,
        help: {
          what: 'Ajusta la capacidad según cuánta gente emplea.',
          where: 'Uno de los seis multiplicadores. Si el cliente no alcanza el tramo más bajo, **no se queda en 1**: aplica el valor del grupo «Cuando ningún tramo aplica», arriba.',
          example:
            'Con el tramo «≥100 → 1.15», una empresa de 150 empleados sube 15%. Una de 4 empleados no cae en ningún tramo y recibe 0.90 — igual que una de la que no se pudo saber el dato, que es el caso más común: hoy el 88% de los análisis recibe este 0.90, y en la mayoría es porque el proveedor no entregó el número de empleados, no porque el cliente sea chico.',
          effect:
            'Los empleados salen de las retenciones del SAT, así que es también un indicio de formalidad. Cuidado al subir el umbral del tramo más bajo: todo lo que quede por debajo cae al valor sin tramo.',
        },
      },
      {
        path: 'riskMultipliers.customerConcentration.tiers',
        label: 'Concentración de clientes',
        hint: 'Del cliente principal, en porcentaje.',
        thresholdLabel: 'concentración',
        thresholdUnit: '%',
        valueLabel: 'Factor',
        valueStep: 0.01,
        help: {
          what: 'Castiga depender de pocos clientes.',
          where: 'Uno de los seis multiplicadores.',
          example: 'Con «≥50% → 0.8», quien le factura el 55% a un solo cliente pierde 20% de capacidad.',
          effect: 'Si ese cliente se va, el arrendatario deja de pagar. Endurecerlo protege la cartera; suavizarlo abre a empresas de nicho.',
        },
      },
      {
        path: 'riskMultipliers.supplierConcentration.tiers',
        label: 'Concentración de proveedores',
        hint: 'Del proveedor principal, en porcentaje.',
        thresholdLabel: 'concentración',
        thresholdUnit: '%',
        valueLabel: 'Factor',
        valueStep: 0.01,
        help: {
          what: 'Castiga depender de pocos proveedores.',
          where: 'Uno de los seis multiplicadores.',
          example: 'Con «≥50% → 0.85», quien le compra el 60% a uno solo pierde 15%.',
          effect: 'Riesgo de suministro: si el proveedor falla, la operación se detiene. Suele castigarse menos que la concentración de clientes.',
        },
      },
      {
        path: 'riskMultipliers.margin.tiers',
        label: 'Por margen de utilidad',
        hint: 'En proporción: 0.30 equivale a 30%.',
        thresholdLabel: 'margen',
        thresholdStep: 0.01,
        valueLabel: 'Factor',
        valueStep: 0.01,
        help: {
          what: 'Premia la rentabilidad.',
          where:
            'Uno de los seis multiplicadores, y además define el plazo. Si el cliente no alcanza el tramo más bajo, **no se queda en 1**: aplica el valor del grupo «Cuando ningún tramo aplica», arriba.',
          example: 'Con «≥0.30 → 1.1», un margen del 32% suma 10% de capacidad. Un margen del 12% no cae en ningún tramo y recibe 0.85.',
          effect:
            'Es el único parámetro que toca dos cosas a la vez: el monto y el plazo. Moverlo tiene doble efecto. Y ojo con el compuesto: con los valores de hoy, una empresa chica de margen fino arranca en 0.90 × 0.85 = 0.765 antes de que entre ningún otro factor.',
        },
      },
    ],
  },
  {
    key: 'rateTable',
    docAnchor: 'oferta',
    label: 'Tabla de tasas',
    icon: 'fa-solid fa-percent',
    hint: 'La tasa anual mejora por score y empeora por listas negras.',
    help:
      'La tasa **no depende del monto ni del plazo**: sale solo del score y de las listas negras. Se busca el primer tramo cuyo umbral alcance el score del cliente; si no alcanza ninguno, aplica la tasa base. Después, si hay listas negras, se le suma el recargo. ' +
      'La oferta conservadora parte de esta misma tasa y le resta su descuento.',
    groups: [
      {
        fields: [
          {
            path: 'rateTable.baseRate',
            label: 'Tasa base',
            unit: '%',
            decimals: 1,
            danger: 'Es la tasa que reciben TODOS los clientes cuyo score no alcanza ningún tramo, incluidos los que no tienen score.',
            help: {
              what: 'La tasa anual que aplica cuando el score no alcanza ningún tramo.',
              where: 'Es el valor por defecto de la búsqueda por tramos.',
              example: 'Con 30% y tramos que arrancan en 650, un cliente con score 600 paga 30%.',
              effect: 'Es el techo de la tabla. Bajarla abarata a los peores perfiles.',
            },
          },
          {
            path: 'rateTable.blacklistSurchargePts',
            label: 'Recargo listas negras',
            unit: 'pts',
            decimals: 1,
            help: {
              what: 'Puntos porcentuales que se SUMAN a la tasa si el cliente aparece en listas negras.',
              where: 'Se aplica después de resolver el tramo.',
              example: 'Con tasa de 29% y recargo de 2.5, la tasa final es 31.5%.',
              effect: 'Es acumulativo con el castigo de capacidad: el cliente recibe menos dinero y más caro.',
            },
          },
        ],
      },
    ],
    tierGroups: [
      {
        path: 'rateTable.tiers',
        label: 'Score → tasa anual',
        hint: 'Se evalúa de arriba a abajo y gana el primero que se cumple.',
        thresholdLabel: 'score',
        valueLabel: 'Tasa',
        valueUnit: '%',
        valueStep: 0.1,
        help: {
          what: 'La tabla que traduce score a tasa.',
          where: 'Primer tramo cuyo umbral alcance el score del cliente.',
          example: 'Con «≥700 → 28%», un score de 715 paga 28%.',
          effect: 'Es donde se decide el precio del producto. Un tramo de más o de menos cambia el margen de toda una franja de clientes.',
        },
      },
    ],
  },
  {
    key: 'terms',
    docAnchor: 'oferta',
    label: 'Plazos',
    icon: 'fa-solid fa-calendar-days',
    hint: 'El plazo sale del margen de utilidad; el conservador resta meses.',
    help: 'El plazo se decide por el **margen de utilidad**, no por el monto ni por el score: la lógica es que un negocio más rentable aguanta un compromiso más largo. Se resuelve el plazo agresivo por tramos y el conservador se obtiene restándole meses, con un piso.',
    groups: [
      {
        fields: [
          {
            path: 'terms.defaultMonths',
            label: 'Plazo por defecto',
            unit: 'meses',
            decimals: 0,
            help: {
              what: 'El plazo agresivo por defecto.',
              where: 'Valor de respaldo de la búsqueda por tramos de margen.',
              example: 'Con 36, un cliente con margen del 10% recibe 36 meses.',
              effect: 'Es el plazo de los perfiles menos rentables.',
            },
          },
          {
            path: 'terms.conservativeDeltaMonths',
            label: 'Meses que resta',
            unit: 'meses',
            decimals: 0,
            help: {
              what: 'Cuántos meses menos tiene la oferta conservadora respecto de la máxima.',
              where: 'plazo conservador = plazo agresivo − este número.',
              example: 'Con plazo agresivo de 48 y delta de 12, el conservador es de 36.',
              effect: 'Ensancha o angosta la distancia entre las dos ofertas que ve el cliente.',
            },
          },
          {
            path: 'terms.conservativeFloorMonths',
            label: 'Piso del plazo',
            unit: 'meses',
            decimals: 0,
            help: {
              what: 'El plazo mínimo de la oferta conservadora.',
              where: 'Se aplica después de restar el delta.',
              example: 'Con plazo agresivo de 36 y delta de 12, el conservador daría 24; con un piso de 30 se queda en 30.',
              effect: 'Evita ofertas conservadoras con plazos impagables.',
            },
          },
        ],
      },
    ],
    tierGroups: [
      {
        path: 'terms.tiers',
        label: 'Margen → plazo',
        hint: 'El margen en proporción: 0.25 equivale a 25%.',
        thresholdLabel: 'margen',
        thresholdStep: 0.01,
        valueLabel: 'Meses',
        valueStep: 1,
        help: {
          what: 'La tabla que traduce margen a plazo.',
          where: 'Primer tramo cuyo umbral alcance el margen del cliente.',
          example: 'Con «≥0.25 → 60», un margen del 28% da 60 meses.',
          effect: 'Alargar plazos aumenta el monto de la cartera pero también la exposición en el tiempo.',
        },
      },
    ],
  },
  {
    key: 'conservativeOffer',
    docAnchor: 'oferta',
    label: 'Oferta conservadora',
    icon: 'fa-solid fa-shield',
    hint: 'Se deriva de la máxima, no se calcula aparte.',
    help:
      'Al cliente se le presentan dos ofertas. La máxima es el resultado del cálculo completo; la conservadora **se deriva de ella**: un porcentaje del monto, una tasa ligeramente mejor y un plazo más corto. ' +
      'La idea es dar un punto de comparación con menos exposición para nosotros y menos compromiso mensual para él. También hay un piso, para que una empresa con facturación real no reciba una oferta simbólica solo porque sus otras señales son pobres. ' +
      'El piso tiene un **techo**: nunca deja la conservadora por encima de cierta fracción de la máxima, así que las dos ofertas siempre salen distintas y la conservadora siempre es la menor.',
    groups: [
      {
        fields: [
          {
            path: 'conservativeOffer.minAmountFactor',
            label: 'Fracción de la máxima',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Qué proporción de la oferta máxima constituye la conservadora.',
              where: 'monto conservador = monto máximo × este factor.',
              example: 'Con máxima de $15 M y 0.45, la conservadora es de $6.7 M.',
              effect: 'Acerca o aleja las dos ofertas. Muy cerca, la conservadora pierde sentido; muy lejos, parece un castigo.',
            },
          },
          {
            path: 'conservativeOffer.minAmountFactorWithBlacklist',
            label: 'Con listas negras',
            unit: '×',
            decimals: 2,
            help: {
              what: 'La misma fracción, más castigada, si el cliente está señalado.',
              where: 'Sustituye a la anterior cuando hay listas negras.',
              example: 'Con 0.35 en vez de 0.45, la conservadora baja otro 22%.',
              effect: 'Se suma a los otros dos castigos por listas negras: menos capacidad y más tasa.',
            },
          },
          {
            path: 'conservativeOffer.rateDiscountPts',
            label: 'Descuento de tasa',
            unit: 'pts',
            decimals: 1,
            help: {
              what: 'Puntos porcentuales que se RESTAN a la tasa en la oferta conservadora.',
              where: 'tasa conservadora = tasa máxima − este número, con piso.',
              example: 'Con tasa de 29% y descuento de 0.8, la conservadora es de 28.2%.',
              effect: 'Es el incentivo para que el cliente elija la opción de menos exposición.',
            },
          },
          {
            path: 'conservativeOffer.minRateFloorPct',
            label: 'Piso de tasa',
            unit: '%',
            decimals: 1,
            danger: 'Ninguna oferta puede quedar por debajo de este piso, ni siquiera con el descuento aplicado.',
            help: {
              what: 'La tasa mínima absoluta de la oferta conservadora.',
              where: 'Se aplica después del descuento.',
              example: 'Con tasa de 25% y descuento de 0.8, el piso de 25% impide que baje a 24.2%.',
              effect: 'Protege el margen del producto frente a la combinación de un buen score y el descuento.',
            },
          },
          {
            path: 'conservativeOffer.revenueFloorFactor',
            label: 'Piso sobre ingreso',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Monto mínimo de la oferta conservadora, como proporción del ingreso anual.',
              where: 'Se toma el mayor entre el cálculo normal y este piso, sin pasar del techo (el campo siguiente).',
              example: 'Con ingresos de $12 M y 0.05, la conservadora no baja de $600 mil, salvo que el techo la detenga antes.',
              effect:
                'Evita ofertas simbólicas a empresas que sí facturan pero tienen señales pobres. Ojo: el piso NO pasa por los multiplicadores de riesgo. Si se recorta la capacidad base sin bajar el piso, el piso alcanza a la máxima en casi todos los clientes de riesgo alto y la conservadora queda pegada al techo.',
            },
          },
          {
            path: 'conservativeOffer.maxShareOfMax',
            label: 'Techo sobre la máxima',
            unit: '×',
            decimals: 2,
            help: {
              what: 'Lo más alto que puede quedar la oferta conservadora, como fracción de la oferta máxima.',
              where: 'Se aplica al final: conservadora = el mayor entre (máxima × fracción) y el piso sobre ingreso, pero nunca más que máxima × este techo.',
              example: 'Con máxima de $371 mil, piso de $2.7 M y techo de 0.80, la conservadora es de $297 mil (antes salía en $2.7 M, por encima de la máxima).',
              effect:
                'Mueve el monto conservador de los clientes cuyo piso por ingreso alcanza a la máxima. Ese monto se ve en la ficha del análisis (Algoritmo y Análisis Fiscal CIEC), es el inicio del rango con el que se decide el arrendamiento («Aprobado en rango», «con diferencia»), es el monto de la preoferta automática y da la renta con la que la Calculadora de riesgo mide la capacidad de pago. Solo cambia los análisis nuevos: los ya hechos conservan su oferta. Va de la fracción de la máxima (no puede quedar debajo) a 0.95: nunca llega a 1, para que las dos ofertas no salgan iguales. Si al simular muchos análisis quedan en el techo, baja el piso, no subas el techo.',
            },
          },
        ],
      },
    ],
  },
  {
    key: 'grades',
    docAnchor: 'oferta',
    label: 'Grades',
    icon: 'fa-solid fa-a',
    hint: 'Solo presentación: no entran al cálculo del monto ni de la tasa.',
    help:
      'La letra que acompaña al score en la pestaña Algoritmo del expediente. **No participa en ningún cálculo**: no cambia el monto, ni la tasa, ni el plazo. Sirve para que el Comité lea de un vistazo en qué franja cae un cliente. ' +
      'Cambiarla es seguro en el sentido de que no mueve dinero, pero sí cambia cómo se percibe un expediente.',
    groups: [],
    tierGroups: [
      {
        path: 'grades.tiers',
        label: 'Score → grade',
        hint: 'La letra que se muestra junto al score.',
        thresholdLabel: 'score',
        valueLabel: 'Grade',
        mode: 'grade',
        help: {
          what: 'La tabla que traduce score a letra.',
          where: 'Solo presentación.',
          example: 'Con «≥750 → A», un score de 780 se muestra como A.',
          effect: 'No mueve dinero, pero sí la percepción del expediente en Comité.',
        },
      },
    ],
  },
  {
    key: 'signalInterpretation',
    docAnchor: 'algoritmo',
    label: 'Interpretación de las señales',
    icon: 'fa-solid fa-triangle-exclamation',
    hint: 'Cómo se lee el reporte del SAT. No son montos ni tasas, pero cambian las ofertas.',
    help:
      'Estos interruptores no ajustan un número: cambian **cómo se interpreta el dato que devuelve Syntage**. Están aquí, en la configuración versionada, precisamente porque alteran las ofertas y merecen lo mismo que el resto: quedar registrados con su motivo, poder simularse contra cartera real antes de activarse, y revertirse con un clic. ' +
      'Los dos vienen **apagados**, reproduciendo el comportamiento con el que la plataforma ha venido operando. Encenderlos es una decisión consciente. ' +
      'Antes de activar cualquiera de los dos, usa **Simular sobre cartera real**: te dice, cliente por cliente, cuánto cambiaría su oferta.',
    groups: [
      {
        fields: [
          {
            path: 'signalInterpretation.strictBlacklistDetection',
            docAnchor: 'algoritmo',
            label: 'Detección estricta de listas negras',
            kind: 'switch',
            decimals: 0,
            onWarning:
              'Al ENCENDER: dejan de castigarse los clientes que solo tienen contrapartes facturadas. Solo señalan los estatus 69-B «presumed» y «definitive» y las contrapartes que Syntage marca. Las ofertas nuevas de esos clientes SUBEN —hasta 66% más de monto— y su tasa BAJA el recargo por listas negras.',
            offWarning:
              'Al APAGAR: se vuelve al criterio con el que la plataforma ha operado, donde basta tener contrapartes facturadas para recibir el castigo completo: capacidad al 60%, tasa con el recargo, y oferta conservadora más chica. Se sabe que castiga a clientes limpios.',
            help: {
              what: 'De dónde se lee si el cliente carga riesgo de listas negras.',
              where: 'Decide el multiplicador de listas negras, el recargo de tasa y la fracción de la oferta conservadora.',
              example: 'Un cliente con seis contrapartes, ninguna señalada: apagado recibe factor 0.60; encendido, 1.00.',
              effect: 'Encenderlo corrige un castigo que hoy reciben clientes limpios. Es el interruptor con más impacto sobre las ofertas.',
            },
          },
          {
            path: 'signalInterpretation.rejectZeroCapacity',
            docAnchor: 'no-alcanza',
            label: 'Rechazar cuando no hay capacidad',
            kind: 'switch',
            decimals: 0,
            onWarning:
              'Al ENCENDER: si el reporte no alcanza para estimar capacidad, la verificación queda FALLIDA con su motivo visible y no se emite oferta. El conteo de verificaciones exitosas va a BAJAR — es la realidad saliendo a la superficie, no una avería.',
            offWarning: 'Al APAGAR: cuando no hay datos suficientes se emite una oferta de $0 y la verificación se marca como exitosa. Es indistinguible de una oferta real y así llegó al Comité.',
            help: {
              what: 'Qué hacer cuando el reporte no alcanza para calcular capacidad. Hace **dos cosas**: decide si se rechaza la oferta hueca y, además, es lo único que produce el fallo «sin datos suficientes» con el que se consulta al proveedor de respaldo.',
              where: 'Actúa antes y después del cálculo: si las señales vienen vacías, o si la capacidad resulta cero.',
              example: 'Un cliente con 152 meses de ventas que suman $0: apagado emite oferta de $0 «exitosa»; encendido, falla con motivo.',
              effect:
                'Encenderlo evita que una oferta sin respaldo llegue al Comité pareciendo legítima. Enciéndelo ANTES que el respaldo: mientras siga apagado, el disparador «Cuando el principal no trae datos suficientes» no se activa aunque su interruptor lo diga.',
            },
          },
        ],
      },
    ],
  },
  {
    key: 'rounding',
    docAnchor: 'oferta',
    label: 'Redondeo',
    icon: 'fa-solid fa-calculator',
    hint: 'Decimales de las tasas en la respuesta.',
    help: 'Cuántos decimales lleva la tasa cuando se guarda y se muestra. No cambia el cálculo, solo su presentación.',
    groups: [
      {
        fields: [
          {
            path: 'rounding.rateDecimals',
            label: 'Decimales',
            unit: 'decimales',
            decimals: 0,
            help: {
              what: 'Cuántos decimales conserva la tasa.',
              where: 'Se aplica al final, sobre las dos ofertas.',
              example: 'Con 2, una tasa de 29.16666 se guarda como 29.17.',
              effect: 'Puramente cosmético.',
            },
          },
        ],
      },
    ],
  },
]

/** Todos los campos escalares, aplanados. Para validar sin recorrer bloques ni grupos. */
export const ALL_FIELDS: FieldDef[] = FIELD_SCHEMA.flatMap((b) => b.groups.flatMap((g) => g.fields))
